import { FilePreview } from "@/components/agent-fs/FilePreview";
import { FileTree } from "@/components/agent-fs/FileTree";
import { buildTreeFromManifest } from "@/components/agent-fs/FilesystemPanel";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { useAgentStore } from "@/lib/agent";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Brain } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/assistant/memory")({
	component: AssistantMemory,
});

const aiClient = createClient(AIService, transport);

function AssistantMemory() {
	const { t } = useTranslation("agent-fs");
	const fsPreviewPath = useAgentStore((s) => s.fsPreviewPath);
	const sessionId = useAgentStore((s) => s.sessionId);

	const { data: memoryFiles } = useQuery({
		queryKey: ["agent-memory-files", sessionId],
		queryFn: async () => {
			const res = await aiClient.listAgentSessionFiles({
				sessionId,
				tier: "memory",
			});
			return res.files.map((f) => ({
				path: f.path,
				tier: f.tier,
				size: Number(f.size),
				modified: f.modifiedAt ? f.modifiedAt.toDate().toISOString() : "",
				sha256: f.sha256,
			}));
		},
		enabled: !!sessionId,
	});

	const treeRoots = useMemo(
		() => buildTreeFromManifest(memoryFiles ?? []),
		[memoryFiles],
	);

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2">
				<Brain className="size-4 text-purple-500" />
				<h2 className="text-sm font-medium">{t("memory_title")}</h2>
				<span className="text-xs text-muted-foreground">
					{t("memory_hint")}
				</span>
			</div>

			<div className="flex rounded-lg border overflow-hidden h-[500px]">
				<div className="w-64 shrink-0 border-r overflow-y-auto">
					<FileTree roots={treeRoots} />
				</div>
				<div className="flex-1 overflow-hidden">
					{fsPreviewPath ? (
						<FilePreview
							sessionId={sessionId}
							path={fsPreviewPath}
							tier="memory"
						/>
					) : (
						<div className="flex items-center justify-center h-full text-xs text-muted-foreground">
							{t("select_file")}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
